import React, { useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import NotificationBell from "@/components/NotificationBell";
import { 
  LogOut, Menu, X, Gamepad2, Plus, Search, Building2, 
  Trophy, Star, BookOpen, Megaphone, Smartphone, Mail, Camera, Award, Users
} from "lucide-react";

const NAV_ITEMS = [
  { id: "how-it-works", labelTop: "How It", labelBottom: "Works", icon: Gamepad2 },
  { id: "host-game", labelTop: "Host a", labelBottom: "Game", icon: Plus },
  { id: "find-games", labelTop: "Find", labelBottom: "Games", icon: Search },
  { id: "featured", labelTop: "Featured", labelBottom: "Games", icon: Trophy },
  { id: "membership", label: "Membership", icon: Star },
  { id: "discover", label: "Players", icon: Users, isPage: true, path: "/discover" },
  { id: "leaderboard", label: "Leaderboard", icon: Award, isPage: true, path: "/leaderboard" },
  { id: "rules", label: "Rules", icon: BookOpen, isPage: true, path: "/rules" },
  { id: "sponsors", label: "Sponsors", icon: Megaphone, isPage: true, path: "/sponsors" },
  { id: "venues", label: "Venues", icon: Building2, isPage: false },
  { id: "apps", label: "Apps", icon: Smartphone, isPage: true, path: "/apps" },
  { id: "contact", label: "Contact", icon: Mail, isPage: true, path: "/contact" },
];

const SharedHeader = ({ activeSection, setActiveSection, mobileMenuOpen, setMobileMenuOpen, onLogoClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, token, setUser } = useAuth();
  const fileInputRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleNavClick = (item) => {
    if (item.isPage) {
      navigate(item.path);
    } else {
      // If we're not on home page, navigate there first
      if (location.pathname !== "/home") {
        navigate("/home", { state: { section: item.id } });
      } else {
        setActiveSection(item.id);
      }
    }
    if (setMobileMenuOpen) setMobileMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (location.pathname === "/home") {
      // Reset to hero view
      setActiveSection(null);
      if (onLogoClick) onLogoClick();
    } else {
      navigate("/home");
    }
  };

  const isActive = (item) => {
    if (item.isPage) {
      return location.pathname === item.path;
    }
    return location.pathname === "/home" && activeSection === item.id;
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PNG, JPG, or GIF file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      try {
        const response = await axios.post(`${API}/auth/update-avatar`, 
          { avatar: base64 },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        toast.success("Avatar updated successfully!");
        // Update local user state
        if (setUser && user) {
          setUser({ ...user, avatar: base64 });
        }
        // Store in localStorage for persistence
        localStorage.setItem("b2b_avatar", base64);
      } catch (err) {
        toast.error("Failed to update avatar");
      }
    };
    reader.readAsDataURL(file);
  };

  // Get avatar from localStorage or user object
  const avatar = localStorage.getItem("b2b_avatar") || user?.avatar;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-transparent backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo - 40% larger (84px) and visible */}
          <div 
            className="flex items-center cursor-pointer flex-shrink-0"
            onClick={handleLogoClick}
            data-testid="header-logo-container"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_hoop-invite/artifacts/r8lcaocz_Board2Boston%20favicon.png"
              alt="Board2Boston"
              style={{ height: '84px', width: 'auto' }}
              data-testid="header-logo"
            />
          </div>

          {/* Desktop Navigation - reduced gap by 10% */}
          <nav className="hidden xl:flex items-center" style={{ gap: '2px' }}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`px-2 py-1 font-ubuntu text-xs transition-colors text-center leading-tight whitespace-nowrap ${
                  isActive(item)
                    ? "text-[#7a6200]"
                    : "text-[#A1A1AA] hover:text-white"
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.labelTop ? (
                  <span className="flex flex-col items-center">
                    <span>{item.labelTop}</span>
                    <span>{item.labelBottom}</span>
                  </span>
                ) : (
                  item.label
                )}
              </button>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-4 ml-8">
            {/* Notification Bell */}
            <NotificationBell token={token} />

            {/* Avatar - links to profile */}
            <div className="relative group">
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif"
                className="hidden"
                onChange={handleAvatarUpload}
                data-testid="avatar-input"
              />
              <button
                onClick={() => navigate("/profile")}
                className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-[#7a6200]/50 hover:border-[#7a6200] transition-colors"
                data-testid="avatar-button"
                title="View Profile"
              >
                {avatar ? (
                  <img src={avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-[#7a6200]/20 flex items-center justify-center">
                    <span className="text-[#7a6200] font-bebas text-lg">
                      {user?.full_name?.charAt(0) || user?.email?.charAt(0) || "?"}
                    </span>
                  </div>
                )}
              </button>
            </div>

            <div className="hidden md:block text-right">
              <button 
                onClick={() => navigate("/profile")}
                className="text-white font-ubuntu text-sm hover:text-[#7a6200] transition-colors"
              >
                {user?.full_name || user?.email}
              </button>
              <p className="text-[#52525B] text-xs">
                {user?.verification_status === "verified" ? (
                  <span className="text-green-400">Verified Member</span>
                ) : (
                  <span className="text-yellow-400">Pending Verification</span>
                )}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#A1A1AA] hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              className="xl:hidden text-white"
              onClick={() => setMobileMenuOpen && setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="xl:hidden border-t border-white/10 bg-[#0A0A0A]">
          <nav className="px-4 py-4 space-y-2">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded font-ubuntu text-sm transition-colors ${
                  isActive(item)
                    ? "bg-[#7a6200]/20 text-[#7a6200]"
                    : "text-[#A1A1AA] hover:bg-white/5"
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.labelTop ? `${item.labelTop} ${item.labelBottom}` : item.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export { SharedHeader, NAV_ITEMS };
export default SharedHeader;
